import { Button, ColorPicker, Empty, Image, Input, InputNumber, Select, Space, Switch, Typography } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import type { ReactNode } from 'react';
import type { FlyerDocElement } from '@/types/flyerDoc';
import { clampRect, round3 } from '@/lib/flyer-canvas';

interface ElementInspectorProps {
  element: FlyerDocElement | null;
  onChange: (el: FlyerDocElement) => void;
  onBump: (id: string, dir: 1 | -1) => void;
  onReplaceImage: (id: string) => void;
}

const Row = ({ label, children }: { label: string; children: ReactNode }) => (
  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
    <Typography.Text type="secondary" style={{ fontSize: 12 }}>{label}</Typography.Text>
    {children}
  </Space>
);

export function ElementInspector({ element: el, onChange, onBump, onReplaceImage }: ElementInspectorProps) {
  if (!el) return <Empty description="Select an element" image={Empty.PRESENTED_IMAGE_SIMPLE} />;

  const setRect = (part: Partial<{ x: number; y: number; w: number; h: number }>) => {
    const r = clampRect({ x: el.x_percent, y: el.y_percent, w: el.width_percent, h: el.height_percent, ...part });
    onChange({ ...el, x_percent: round3(r.x), y_percent: round3(r.y), width_percent: round3(r.w), height_percent: round3(r.h) });
  };
  const setStyle = (patch: Record<string, unknown>) => onChange({ ...el, style: { ...el.style, ...patch } });
  const pct = (value: number, write: (v: number) => void) => (
    <InputNumber size="small" style={{ width: 90 }} min={0} max={100} step={0.5}
      value={Math.round(value * 1000) / 10} onChange={(v) => write((v ?? 0) / 100)} />
  );

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={6}>
      <Row label="X">{pct(el.x_percent, (v) => setRect({ x: v }))}</Row>
      <Row label="Y">{pct(el.y_percent, (v) => setRect({ y: v }))}</Row>
      <Row label="Width">{pct(el.width_percent, (v) => setRect({ w: v }))}</Row>
      <Row label="Height">{pct(el.height_percent, (v) => setRect({ h: v }))}</Row>
      <Row label="Layer">
        <Space size={4}>
          <Button size="small" icon={<UpOutlined />} onClick={() => onBump(el.id, 1)} />
          <Button size="small" icon={<DownOutlined />} onClick={() => onBump(el.id, -1)} />
        </Space>
      </Row>
      {el.kind === 'text' && (
        <>
          <Input.TextArea rows={3} value={el.text} onChange={(e) => onChange({ ...el, text: e.target.value })} placeholder="Text…" />
          <Row label="Font size (px)">
            <InputNumber size="small" style={{ width: 90 }} min={8} max={800} value={el.style?.fontSize ?? 48}
              onChange={(v) => setStyle({ fontSize: v ?? 48 })} />
          </Row>
          <Row label="Color">
            <ColorPicker size="small" value={el.style?.color ?? '#000000'} onChangeComplete={(v) => setStyle({ color: v.toHexString() })} />
          </Row>
          <Row label="Align">
            <Select size="small" style={{ width: 90 }} value={el.style?.alignment ?? 'left'}
              options={[{ value: 'left' }, { value: 'center' }, { value: 'right' }]}
              onChange={(v) => setStyle({ alignment: v })} />
          </Row>
          <Row label="Bold"><Switch size="small" checked={el.style?.fontWeight === 'bold'} onChange={(v) => setStyle({ fontWeight: v ? 'bold' : 'regular' })} /></Row>
          <Row label="Italic"><Switch size="small" checked={!!el.style?.isItalic} onChange={(v) => setStyle({ isItalic: v })} /></Row>
        </>
      )}
      {el.kind === 'image' && (
        <>
          {el.src_url && <Image src={el.src_url} style={{ maxHeight: 100, objectFit: 'contain' }} />}
          <Button size="small" onClick={() => onReplaceImage(el.id)}>Replace image…</Button>
        </>
      )}
    </Space>
  );
}
